import { type FC, useEffect, useRef, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { PollResultsModeType } from "@/types/graphql/partner.generated";
import {
  pollErrorKey,
  useCreatePollMutation,
  usePollQuery,
  useUpdatePollMutation,
  type LibraryPoll,
} from "@/partner/entities/polls";
import {
  emptyPollLibraryForm,
  POLL_LIBRARY_FORM_ERROR_KEYS,
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POLL_RESULTS_MODE_LABEL_KEYS,
  pollToLibraryFormValues,
  toCreatePollInput,
  toUpdatePollInput,
  validatePollLibraryForm,
  type PollLibraryFormValues,
  type PollOptionFormValue,
} from "../model/pollLibraryForm";
import { pollDialogState } from "../model/pollDialogState";

interface PollLibraryFormProps {
  /** null: новый опрос. */
  poll: LibraryPoll | null;
  onClose: () => void;
}

const PollLibraryForm: FC<PollLibraryFormProps> = ({ poll, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues] = useState<PollLibraryFormValues>(() =>
    poll ? pollToLibraryFormValues(poll) : emptyPollLibraryForm()
  );
  const [submitted, setSubmitted] = useState(false);
  const locked = Boolean(poll?.usage.isLocked);
  const error = validatePollLibraryForm(values);

  const onCompleted = () => {
    enqueueSnackbar(t("pollsLibrary.snackbar.saved"), { variant: "success" });
    onClose();
  };
  const onError = (mutationError: Error) =>
    enqueueSnackbar(t(pollErrorKey(mutationError)), { variant: "error" });
  const [createPoll, { loading: creating }] = useCreatePollMutation({
    onCompleted,
    onError,
  });
  const [updatePoll, { loading: updating }] = useUpdatePollMutation({
    onCompleted,
    onError,
  });

  const set = <K extends keyof PollLibraryFormValues>(
    key: K,
    value: PollLibraryFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  const setOption = (index: number, patch: Partial<PollOptionFormValue>) =>
    setValues((prev) => ({
      ...prev,
      options: prev.options.map((option, i) =>
        i === index ? { ...option, ...patch } : option
      ),
    }));

  const handleSubmit = () => {
    setSubmitted(true);
    if (error) return;
    if (poll) {
      void updatePoll({
        variables: { id: poll.id, input: toUpdatePollInput(values, locked) },
      });
    } else {
      void createPoll({ variables: { input: toCreatePollInput(values) } });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {poll
              ? t("pollsLibrary.dialog.editTitle")
              : t("pollsLibrary.dialog.createTitle")}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            disabled={creating || updating}
          >
            {t("common.actions.save")}
          </Button>
        </Toolbar>
        <Divider />
      </AppBar>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {poll && (
            <Typography variant="body2" color="text.secondary">
              {t("pollsLibrary.dialog.usage", {
                attachments: poll.usage.attachmentsCount,
              })}
            </Typography>
          )}
          {locked && (
            <Alert severity="info">{t("pollsLibrary.dialog.lockedHint")}</Alert>
          )}
          <TextField
            label={t("polls.form.question")}
            value={values.question}
            onChange={(event) => set("question", event.target.value)}
            fullWidth
            multiline
          />
          <Box>
            <Typography variant="subtitle2">
              {t("polls.form.options")}
            </Typography>
            {values.options.map((option, index) => (
              <Stack
                key={option.id ?? `new-${index}`}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <TextField
                  size="small"
                  fullWidth
                  disabled={locked}
                  value={option.text}
                  placeholder={t("polls.form.optionPlaceholder")}
                  onChange={(event) =>
                    setOption(index, { text: event.target.value })
                  }
                />
                <FormControlLabel
                  disabled={locked}
                  control={
                    <Checkbox
                      checked={option.isCorrect}
                      onChange={(event) =>
                        setOption(index, { isCorrect: event.target.checked })
                      }
                    />
                  }
                  label={t("polls.form.correct")}
                />
                <IconButton
                  aria-label={t("common.actions.delete")}
                  disabled={locked || values.options.length <= POLL_MIN_OPTIONS}
                  onClick={() =>
                    setValues((prev) => ({
                      ...prev,
                      options: prev.options.filter((_, i) => i !== index),
                    }))
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              sx={{ mt: 1 }}
              disabled={locked || values.options.length >= POLL_MAX_OPTIONS}
              onClick={() =>
                setValues((prev) => ({
                  ...prev,
                  options: [
                    ...prev.options,
                    { id: null, text: "", isCorrect: false },
                  ],
                }))
              }
            >
              {t("polls.form.addOption")}
            </Button>
          </Box>
          <FormControlLabel
            disabled={locked}
            control={
              <Switch
                checked={values.isMultiple}
                onChange={(event) => set("isMultiple", event.target.checked)}
              />
            }
            label={t("polls.form.isMultiple")}
          />
          <TextField
            select
            disabled={locked}
            label={t("polls.form.resultsMode")}
            value={values.resultsMode}
            onChange={(event) =>
              set("resultsMode", event.target.value as PollResultsModeType)
            }
            fullWidth
          >
            {Object.values(PollResultsModeType).map((mode) => (
              <MenuItem key={mode} value={mode}>
                {t(POLL_RESULTS_MODE_LABEL_KEYS[mode])}
              </MenuItem>
            ))}
          </TextField>
          {submitted && error && (
            <Typography color="error" variant="body2">
              {t(POLL_LIBRARY_FORM_ERROR_KEYS[error])}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

interface PollLibraryDialogProps {
  /** null: создание; id: правка, опрос читается заново (свежий usage). */
  pollId: string | null;
  onClose: () => void;
}

export const PollLibraryDialog: FC<PollLibraryDialogProps> = ({
  pollId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error } = usePollQuery({
    variables: { id: pollId ?? "" },
    skip: !pollId,
    fetchPolicy: "network-only",
  });
  const poll = data?.poll ?? null;
  const state = pollDialogState({ pollId, loading, hasPoll: Boolean(poll) });

  // Опрос не загрузился (ошибка или его уже удалили): сообщаем и закрываем
  // диалог, иначе на странице остаётся ?tab=edit без видимого окна. Ref
  // не даёт показать ошибку дважды, пока родитель убирает диалог.
  const reported = useRef(false);
  useEffect(() => {
    if (state !== "failed" || reported.current) return;
    reported.current = true;
    enqueueSnackbar(t(pollErrorKey(error)), { variant: "error" });
    onClose();
  }, [state, error, enqueueSnackbar, t, onClose]);

  if (state === "failed") return null;
  if (state === "loading") {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <PollLibraryForm key={poll?.id ?? "new"} poll={poll} onClose={onClose} />
  );
};
